import { useCallback, useEffect, useRef, useState } from "react";
import {
  getSpeechRecognitionCtor,
  getSpeechUnsupportedMessage,
  isSpeechRecognitionSupported,
} from "../utils/speechSupport";
import {
  buildLiveTranscript,
  mergeSpeechSegments,
} from "../utils/speechTranscriptUtils";
import { dedupeTranscript } from "../utils/parseEnquiryTranscript";

const DEFAULT_LANG = "en-IN";

/**
 * Web Speech API hook for CRM voice enquiry.
 * Rebuilds transcript from the results array and collapses browser overlap.
 */
export function useSpeechToText({ lang = DEFAULT_LANG, continuous = true } = {}) {
  const recognitionRef = useRef(null);
  const wantsListeningRef = useRef(false);
  const restartTimerRef = useRef(null);
  const sealedSessionsRef = useRef("");
  const sessionIdRef = useRef(0);

  const [isSupported] = useState(() => isSpeechRecognitionSupported());
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState(null);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  }, []);

  const rebuildTranscriptFromEvent = useCallback((event) => {
    const finalParts = [];
    let interim = "";

    for (let i = 0; i < event.results.length; i += 1) {
      const result = event.results[i];
      const text = String(result[0]?.transcript || "").trim();
      if (!text) continue;

      if (result.isFinal) {
        finalParts.push(text);
      } else {
        // Keep only the latest interim hypothesis; browsers send cumulative chunks.
        interim = text;
      }
    }

    const sessionText = dedupeTranscript(mergeSpeechSegments(finalParts));
    const combined = dedupeTranscript(
      mergeSpeechSegments([sealedSessionsRef.current, sessionText].filter(Boolean))
    );

    setTranscript(combined);
    setInterimTranscript(interim.trim());
  }, []);

  const stop = useCallback(() => {
    wantsListeningRef.current = false;
    clearRestartTimer();
    setIsListening(false);
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
  }, [clearRestartTimer]);

  const start = useCallback(() => {
    setError(null);

    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError(getSpeechUnsupportedMessage());
      return false;
    }

    stop();
    sealedSessionsRef.current = "";
    sessionIdRef.current += 1;
    const activeSessionId = sessionIdRef.current;

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      if (activeSessionId !== sessionIdRef.current) return;
      rebuildTranscriptFromEvent(event);
    };

    recognition.onerror = (event) => {
      const code = event.error;
      if (code === "aborted" || code === "no-speech") return;
      if (code === "not-allowed" || code === "service-not-allowed") {
        setError("Microphone permission denied. Allow mic access and try again.");
        wantsListeningRef.current = false;
        setIsListening(false);
        return;
      }
      if (code === "network") {
        setError("Speech service unavailable. Check internet connection or use manual entry.");
        wantsListeningRef.current = false;
        setIsListening(false);
        return;
      }
      setError("Could not capture voice. Please try again or use manual entry.");
    };

    recognition.onend = () => {
      if (activeSessionId !== sessionIdRef.current) return;

      if (!wantsListeningRef.current) {
        setIsListening(false);
        return;
      }

      setTranscript((prev) => {
        const sealed = dedupeTranscript(prev.trim());
        sealedSessionsRef.current = sealed;
        return sealed;
      });
      setInterimTranscript("");

      clearRestartTimer();
      restartTimerRef.current = setTimeout(() => {
        if (!wantsListeningRef.current || activeSessionId !== sessionIdRef.current) return;
        try {
          recognition.start();
        } catch {
          wantsListeningRef.current = false;
          setIsListening(false);
        }
      }, 300);
    };

    recognitionRef.current = recognition;
    wantsListeningRef.current = true;

    try {
      recognition.start();
      return true;
    } catch {
      setError("Unable to start voice capture. Tap the mic again.");
      wantsListeningRef.current = false;
      setIsListening(false);
      return false;
    }
  }, [clearRestartTimer, continuous, lang, rebuildTranscriptFromEvent, stop]);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
      return;
    }
    start();
  }, [isListening, start, stop]);

  const resetTranscript = useCallback(() => {
    sealedSessionsRef.current = "";
    sessionIdRef.current += 1;
    setTranscript("");
    setInterimTranscript("");
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      wantsListeningRef.current = false;
      sessionIdRef.current += 1;
      clearRestartTimer();
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
    };
  }, [clearRestartTimer]);

  const liveText = buildLiveTranscript(transcript, interimTranscript);

  return {
    isSupported,
    isListening,
    transcript,
    interimTranscript,
    liveText,
    error,
    start,
    stop,
    toggle,
    resetTranscript,
    setTranscript,
  };
}
